import { useId } from 'react';
import styles from './Input.module.css';

/**
 * Generic input component used across forms.
 *
 * 0.36.260 — Aligned with the Henaket text-input spec:
 *
 *     ┌─ Field wrapper ──────────┐
 *     │  Label       (optional)  │  16/24 weight 600 #1A1A1A
 *     │  Hint        (optional)  │  16/24 weight 400 dark-grey
 *     │  ┌───── Input box ─────┐ │  40px, 1px border #0E0D0D, r=4
 *     │  │ Value               │ │
 *     │  └─────────────────────┘ │
 *     │  Summary     (optional)  │  Heading 14/24 + Value 16/24 bold
 *     └──────────────────────────┘
 *
 * Each "line" renders only when its content is non-empty. The
 * Summary block is new in 0.36.260 — opt-in via the
 * `summaryHeading` and `summaryValue` props. Used for showing a
 * computed result below an input (e.g. "Tax (10%) — 300,000").
 *
 * Props:
 *   label, help, helpPosition='above'|'below'
 *   summaryHeading, summaryValue   (Summary block; renders only if either is set)
 *   error: string — show error message
 *   required: bool — show required marker
 *   requiredPosition: 'before' (default, Henaket "*First Name") or
 *     'after' ("First Name *"). 0.38.54 - the DEFAULT swapped from
 *     'after' to 'before' so the Henaket design system applies
 *     platform-wide without callers having to opt in. Existing
 *     callers that WANT the trailing style must now pass
 *     requiredPosition="after" explicitly; none in this codebase
 *     did as of 0.38.54, so the swap is invisible to existing
 *     callers.
 */
export default function Input({
  label, help, helpPosition = 'above', error,
  summaryHeading, summaryValue,
  required = false, requiredPosition = 'before',
  // 0.36.460 — Optional pass-through onto the outer .field wrapper
  // so callers that render Input in tight layouts (inline row-edit
  // cells) can override the default margin-bottom without touching
  // the shared component's CSS.
  wrapperClassName = '', wrapperStyle,
  // 0.36.586 — Multi-line mode. When `multiline` is true, renders
  // a `<textarea>` instead of `<input>`. Height defaults to 2 rows
  // (`rows` prop configurable). All other props (label, error, etc.)
  // behave identically. Used by the Library Settings modal's Short
  // description field (short but occasionally multi-line).
  multiline = false,
  rows,
  // 0.46.75 - A small mark inside the box at its right edge (the ID
  // card mark on verified fields). Anchored to the box itself, so a
  // hint above or below the box does not move it.
  adornment,
  id, ...rest
}) {
  const generatedId = useId();
  const inputId = id || generatedId;
  const helpId = `${inputId}-help`;
  const errorId = `${inputId}-error`;

  const helpNode = help && (
    <span
      className={
        `${styles.help} ${helpPosition === 'below' ? styles.helpBelow : styles.helpAbove}`
      }
      id={helpId}
    >{help}</span>
  );

  const asterisk = required && (
    <span className={styles.required} aria-hidden="true">*</span>
  );

  // 0.36.260 — Summary block. Renders only if at least one of the two
  // props is non-empty. Each inner line is independently optional.
  const hasSummary = !!(summaryHeading || summaryValue);

  return (
    <div
      className={`${styles.field} ${wrapperClassName} ${error ? 'field-error-bar' : ''}`}
      style={wrapperStyle}
    >
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {required && requiredPosition === 'before' && asterisk}
          {label}
          {required && requiredPosition === 'after' && asterisk}
        </label>
      )}
      {helpPosition === 'above' && helpNode}
      {/* 0.36.440 — Henaket per-field error treatment (per spec):
        * the red left bar wraps the ENTIRE field (label + hint/error
        * + input), 4px wide, 20px left padding. The input's own
        * border thickens to 2px red. Was: bar wrapped only the
        * input div (ships 0.35.x - 0.36.437).
        * 0.36.562 — Only render the visible error MESSAGE text when
        * `error` is a string. Passing `error={true}` (boolean) turns
        * on the red-border styling only — used by row-edit cells
        * where per-field error messages would clutter and the
        * message is shown once at the top of the page. */}
      {/* 0.36.600 — Accept string OR ReactNode as `error`. Was
        * `typeof error === 'string'` only; some callers now pass
        * JSX (e.g. italic example in the Library key error). */}
      {error && error !== true && (
        <div className="field-error-text" id={errorId}>
          {error}
        </div>
      )}
      {multiline ? (
        <textarea
          id={inputId}
          className={`${styles.input} ${styles.textarea} ${error ? styles.inputError : ''}`}
          aria-invalid={!!error}
          aria-describedby={[help && helpId, error && errorId].filter(Boolean).join(' ') || undefined}
          required={required}
          rows={rows || 2}
          {...rest}
        />
      ) : adornment ? (
        <div className={styles.adornWrap}>
          <input
            id={inputId}
            className={`${styles.input} ${styles.inputAdorned} ${error ? styles.inputError : ''}`}
            aria-invalid={!!error}
            aria-describedby={[help && helpId, error && errorId].filter(Boolean).join(' ') || undefined}
            required={required}
            {...rest}
          />
          <span className={styles.adornment}>{adornment}</span>
        </div>
      ) : (
        <input
          id={inputId}
          className={`${styles.input} ${error ? styles.inputError : ''}`}
          aria-invalid={!!error}
          aria-describedby={[help && helpId, error && errorId].filter(Boolean).join(' ') || undefined}
          required={required}
          {...rest}
        />
      )}
      {helpPosition === 'below' && helpNode}
      {hasSummary && (
        <div className={styles.summary}>
          {summaryHeading && (
            <span className={styles.summaryHeading}>{summaryHeading}</span>
          )}
          {summaryValue && (
            <span className={styles.summaryValue}>{summaryValue}</span>
          )}
        </div>
      )}
    </div>
  );
}
